import { useEffect, useState } from "react";
import { Fingerprint, ScanFace, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { setAccessToken } from "@/lib/api";
import {
  isBiometricAvailable, hasStoredBiometric, tryBiometricUnlock, getBiometryLabel, disableBiometric,
} from "@/lib/biometric";

/**
 * Shown on the Login page when the device already has a stored biometric credential.
 * Tapping the button prompts Face/Touch ID, restores the session, and navigates home.
 * Silently hidden on web or when no credential has been enrolled yet.
 */
export default function BiometricQuickUnlock() {
  const { checkAuth } = useAuth();
  const [show, setShow] = useState(false);
  const [label, setLabel] = useState("Face ID");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const { available } = await isBiometricAvailable();
      if (!available) return;
      if (!(await hasStoredBiometric())) return;
      setLabel(await getBiometryLabel());
      setShow(true);
    })();
  }, []);

  const unlock = async () => {
    setBusy(true); setError("");
    const result = await tryBiometricUnlock();
    if (!result?.token) {
      setBusy(false);
      setError("Couldn't verify. Please try again or sign in with your password.");
      return;
    }
    setAccessToken(result.token);
    // AuthContext will pick up the new token via checkAuth() and redirect away from /login
    await checkAuth();
    setBusy(false);
  };

  const forget = async () => {
    await disableBiometric();
    setShow(false);
  };

  if (!show) return null;
  const Icon = label === "Face ID" ? ScanFace : Fingerprint;

  return (
    <div className="space-y-3" data-testid="biometric-quick-unlock">
      <Button
        onClick={unlock}
        disabled={busy}
        className="w-full h-12 rounded-full text-base gap-2"
        data-testid="biometric-quick-unlock-button"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
        {busy ? "Unlocking…" : `Unlock with ${label}`}
      </Button>
      {error && (
        <p className="text-xs text-red-600 text-center" data-testid="biometric-quick-unlock-error">{error}</p>
      )}
      <div className="flex items-center gap-2 my-1">
        <div className="flex-1 h-px bg-border" />
        <span className="text-[11px] text-muted-foreground uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <button
        type="button"
        onClick={forget}
        className="text-xs text-muted-foreground hover:text-foreground block mx-auto"
        data-testid="biometric-quick-unlock-forget"
      >
        Not you? Forget this device
      </button>
    </div>
  );
}
